import React, { useEffect } from 'react';
import { Form as FinalForm } from 'react-final-form';
import { Alert, Button } from 'react-bootstrap';
import { t, translate } from 'react-switch-lang';
import { Link } from 'react-router-dom';
import './contactusform.scss';
import './ContactUsPaySimply.scss';
import { useSelector } from 'react-redux';
import { validateName, validateBody } from './Validation';
import { invalidFormAlert } from '../../../helperFunctions/Accessibility';
import RelevantFAQ from './RelevantFAQ';
import ContactFormHeader from './ContactFormHeader';
import useRelevantFAQSearch from '../../../helperFunctions/useRelevantFAQSearch';
import useContactFormSubmit from '../../../helperFunctions/useContactFormSubmit';
import {
  ContactRadioField,
  MailcheckEmailField,
  TextAreaField,
  TextField,
} from '../../../components/FormFields/FormFields';
import ContactReCaptcha from '../../../components/ContactReCaptcha';
import { getContactFormStyle } from '../../../configurations/Brands';
import {
  events,
  trackGoogleAnalyticsEvent,
} from '../../../helperFunctions/GoogleAnalytics';

function ContactUsPartners({ widgetMode }) {
  const { lang } = useSelector((state) => state);
  const { handleTextareaChange, searchResults } = useRelevantFAQSearch({
    brand: 'partners',
    lang,
  });
  const {
    dismissError,
    error,
    errorMessageRef,
    reCaptchaRef,
    showReCaptchaError,
    showError,
    submitted,
    submitContactRequest,
    successMessageRef,
  } = useContactFormSubmit();

  const isMobile = (window.location.href.includes('mobileapp') ? '?mobileapp' : '');

  useEffect(() => {
    trackGoogleAnalyticsEvent(events.CONTACT_FORM_VIEW, {
      brand: 'partners',
      language: lang,
    });
  }, [lang]);

  function validateContactForm(values) {
    const errors = {};
    const businessNameError = validateName(values.businessName || '');
    const descriptionError = validateBody(values.description || '');

    if (businessNameError) errors.businessName = businessNameError;
    if (!values.product) errors.product = t('Validation_Required');
    if (descriptionError) errors.description = descriptionError;

    return errors;
  }

  async function sendAPI(values) {
    const data = {
      Brand: 'PSI Partners',
      BrandID: 'Partners',
      BusinessName: values.businessName,
      Product: values.product,
      CustomerEmail: values.customerEmail,
      Body: values.description,
      Language: lang,
    };

    return submitContactRequest(data);
  }

  useEffect(() => {
    if (widgetMode) {
      document.getElementById('desktop-contact-us').style.padding = '0px';
    }
  }, []);

  return (
    <>
      {showError && (
        <Alert
          ref={errorMessageRef}
          variant="danger"
          id="error_message"
          className="mt-3"
          onClose={dismissError}
          dismissible
        >
          <p style={{ marginBottom: 0 }}>{error}</p>
        </Alert>
      )}
      {submitted && (
        <div
          id="successMessage"
          ref={successMessageRef}
          style={{ display: 'block', visibility: 'visible' }}
        >
          <h1>{t('ContactForm_Submitted')}</h1>
          <Link to={(widgetMode) ? `/widget/partners/${lang}${isMobile}` : `/partners/${lang}/categories`}>
            <Button className="error-go-home-btn partners-button">{t('ReturnHome_Btn')}</Button>
          </Link>
        </div>
      )}
      {!submitted && (
        <div
          id="desktop-contact-us"
          className={widgetMode && 'widget-mode-form'}
          style={getContactFormStyle('partners')}
        >
          <div id="skip-link-content">
            <ContactFormHeader brand="partners" lang={lang} widgetMode={widgetMode} />
            <p className="contact-label-suggestion">{t('ContactForm_Fields_Required')}</p>
            <FinalForm
              onSubmit={sendAPI}
              validate={validateContactForm}
              render={({
                handleSubmit,
                invalid,
                errors,
                submitting,
                touched,
                submitFailed,
              }) => {
                const productError = errors.product;
                const showProductError = (
                  touched?.product || submitFailed
                ) && productError;

                return (
                  <form
                    onSubmit={(event) => {
                      invalidFormAlert(invalid, errors);
                      handleSubmit(event);
                    }}
                    noValidate
                  >
                    <div className="row">
                      <MailcheckEmailField
                        className="col-12 col-md-6"
                        id="ContactForm_General_Email_"
                        name="customerEmail"
                        label={t('ContactForm_General_Email_Label')}
                        formatText={t('ContactForm_General_Email_Help')}
                      />
                    </div>

                    <div className="row">
                      <TextField
                        className="col-12 col-md-6"
                        id="ContactForm_Partners_BusinessName_"
                        name="businessName"
                        label={t('ContactForm_Business_Name')}
                      />
                    </div>

                    <fieldset className="partners-service-options form-field">
                      <legend
                        id="productLabel"
                        className={`field-label ${showProductError ? 'is-invalid' : ''}`}
                      >
                        {t('ContactForm_Product_Title')}
                      </legend>
                      <div className="row no-gutters w-100">
                        <div className="col-12 col-md-4 mb-2 pr-md-1">
                          <ContactRadioField
                            name="product"
                            value="PartnerPay / Interac e-Transfer"
                            className="w-100 h-100"
                            optionClassName="partners-service-option d-flex align-items-center w-100 h-100"
                            content={(
                              <>
                                <img
                                  className="partners-service-icon"
                                  src="/brandIcons/eTransfer.png"
                                  alt=""
                                />
                                <span>
                                  Interac e-Transfer
                                  <sup>®</sup>
                                </span>
                              </>
                            )}
                          />
                        </div>
                        <div className="col-12 col-md-4 mb-2 px-md-1">
                          <ContactRadioField
                            name="product"
                            value="Pay in person at Canada Post"
                            className="w-100 h-100"
                            optionClassName="partners-service-option d-flex align-items-center w-100 h-100"
                            content={(
                              <>
                                <img
                                  className="partners-service-icon"
                                  src="/brandIcons/CanadaPostIcon.png"
                                  alt=""
                                />
                                <span>Pay in person</span>
                              </>
                            )}
                          />
                        </div>
                        <div className="col-12 col-md-4 mb-2 pl-md-1">
                          <ContactRadioField
                            name="product"
                            value="PaySimply"
                            className="w-100 h-100"
                            optionClassName="partners-service-option d-flex align-items-center w-100 h-100"
                            content={(
                              <>
                                <img
                                  className="partners-service-icon partners-service-logo partners-service-logo-red"
                                  src="/brandIcons/PaySimplyLogoRed.png"
                                  alt=""
                                />
                                <img
                                  className="partners-service-icon partners-service-logo partners-service-logo-white"
                                  src="/brandIcons/PaySimply Logo - White.png"
                                  alt=""
                                />
                                <span>Payment Links</span>
                              </>
                            )}
                          />
                        </div>
                      </div>
                      {showProductError && (
                        <span className="errorMessage errorMessageBlock" id="product_error_message">
                          {productError}
                        </span>
                      )}
                    </fieldset>

                    <div className="row">
                      <TextAreaField
                        className="col-12"
                        id="ContactForm_General_Body_"
                        name="description"
                        label={t('ContactForm_General_Body_Label')}
                        onChange={handleTextareaChange}
                      >
                        <div className="sensitive-information-disclaimer">
                          {t('Sensitive_Information_Disclaimer')}
                        </div>
                      </TextAreaField>
                    </div>

                    <RelevantFAQ searchResults={searchResults} brand="partners" lang={lang} />
                    <Button
                      id="submit-button"
                      className="paysimply-button"
                      type="submit"
                      disabled={submitting}
                      size="lg"
                      style={{ backgroundColor: '#2B4E7B' }}
                    >
                      {submitting ? t('ContactForm_Submitting') : t('ContactForm_Submit_button')}
                    </Button>
                    <ContactReCaptcha ref={reCaptchaRef} onErrored={showReCaptchaError} />
                  </form>
                );
              }}
            />
            <Alert className="mt-3 email-banner" show={process.env.REACT_APP_ENV === 'dev'}>
              On the test system, contact form emails will be sent to
              <strong> psidevstar@gmail.com</strong>
            </Alert>
          </div>
        </div>
      )}
    </>
  );
}

export default translate(ContactUsPartners);
